import React, { Component } from "react";
import { get as _get } from "lodash";

function isDirectionsDebug() {
  try {
    return new URLSearchParams(window.location.search).get("debug") === "true";
  } catch (e) {
    return false;
  }
}

/**
 * Leg / crew list order vs Mapbox optimized trip (visit indices only).
 * — Per-seat: leg count vs canvassers with dropoffs; waypoint_index strictly increasing.
 * — route_to_all_sites: leg count vs sites + return; site_order_position vs row order.
 */
export function auditMapboxOrderVsCrew(crew) {
  const legs = _get(crew, "directions.legs", []);
  const issues = [];

  if (crew.route_to_all_sites) {
    const sites = _get(crew, "canvassers[0].sites", []);
    if (!sites.length) {
      issues.push("No sites on crew.");
    }
    const expectedLegs = sites.length + 1;
    if (sites.length && legs.length !== expectedLegs) {
      issues.push(
        `Expected ${expectedLegs} legs (each site segment plus return), got ${legs.length}.`
      );
    }
    sites.forEach((s, i) => {
      const pos = s.site_order_position;
      if (pos != null && Number(pos) !== i + 1) {
        issues.push(
          `Site list row ${i + 1} has site_order_position=${pos}, expected ${i + 1}.`
        );
      }
    });
    return {
      ok: issues.length === 0,
      issues,
      mode: "sites",
    };
  }

  const canvassers = crew.canvassers || [];
  const withDropoff = canvassers.filter(
    (c) =>
      c.dropoff &&
      (c.dropoff.longitude != null || c.dropoff.latitude != null)
  );

  if (!withDropoff.length) {
    return {
      ok: false,
      issues: ["No canvassers with dropoff coordinates."],
      mode: "dropoffs",
    };
  }

  if (legs.length !== withDropoff.length) {
    issues.push(
      `Leg count (${legs.length}) does not match canvassers with dropoffs (${withDropoff.length}).`
    );
  }

  const wpIndices = withDropoff.map((c) =>
    _get(c, "dropoff.waypoint.waypoint_index")
  );
  const missingWpIdx = [];
  wpIndices.forEach((w, i) => {
    if (typeof w !== "number" || !Number.isFinite(w)) missingWpIdx.push(i);
  });
  if (missingWpIdx.length) {
    issues.push(
      `Missing Mapbox waypoint_index on seat(s) ${missingWpIdx
        .map((i) => i + 1)
        .join(", ")} — rebuild crewsheet or clear cache.`
    );
  } else {
    for (let i = 1; i < wpIndices.length; i++) {
      if (wpIndices[i] <= wpIndices[i - 1]) {
        issues.push(
          `Crew list order does not follow Mapbox visit order: waypoint_index [${wpIndices.join(
            ", "
          )}] (each next stop should be a higher index).`
        );
        break;
      }
    }
  }

  return {
    ok: issues.length === 0,
    issues,
    mode: "dropoffs",
    waypointIndices: wpIndices,
  };
}

export default class CrewSheetDrivingDirections extends Component {
  getMilesFromMeters = (meters) => {
    return Math.round((meters / 1609.344) * 100) / 100;
  };

  // duration comes in seconds
  makeDurationReadable = (duration) => {
    if (!duration) return "";

    const total_minutes = Math.round(duration / 60);
    const hours = Math.floor(total_minutes / 60);
    const minutes = total_minutes % 60;
    let result = "";

    if (hours) {
      result += hours + "h ";
    }

    result += minutes + "min";
    return result;
  };

  buildLegLabel(crew, leg, index) {
    const legs = _get(crew, "directions.legs", []);
    const sites = _get(crew, "canvassers[0].sites", []);
    const canvassers = _get(crew, "canvassers", []);

    if (crew.route_to_all_sites) {
      const isReturnLeg = index === legs.length - 1;
      if (isReturnLeg) {
        return { title: "Return to Office", destination: "Office" };
      }
      const site = sites[index] || {};
      return {
        title: site.name || `Site ${index + 1}`,
        destination: site.addressline1
          ? `${site.addressline1}, ${site.city || ""}`
          : "",
      };
    }

    const canvasser = canvassers[index] || {};
    const name = [canvasser.firstname, canvasser.lastname]
      .filter(Boolean)
      .join(" ");
    const dest =
      _get(canvasser, "dropoff.name", "") ||
      _get(canvasser, "dropoff.address", "") ||
      _get(canvasser, "dropoff.addressline1", "") ||
      _get(canvasser, "dropoff.waypoint.name", "");
    const coordFallback =
      !dest && canvasser.dropoff
        ? `${_get(canvasser, "dropoff.latitude", "")}, ${_get(
            canvasser,
            "dropoff.longitude",
            ""
          )}`
        : "";

    return {
      title: name || `Stop ${index + 1}`,
      destination: dest || coordFallback,
    };
  }

  getDirectionsTotals(crew) {
    const directions = _get(crew, "directions", {});
    const legs = _get(directions, "legs", []);
    let duration = directions.duration;
    let distance = directions.distance;
    if (
      (duration == null || distance == null) &&
      legs.length > 0
    ) {
      duration = legs.reduce((sum, leg) => sum + (leg.duration || 0), 0);
      distance = legs.reduce((sum, leg) => sum + (leg.distance || 0), 0);
    }
    return { duration, distance };
  }

  formatCumulativeMiles(meters) {
    return `mile ${this.getMilesFromMeters(meters)}`;
  }

  buildListDirections() {
    let { crew } = this.props;
    let legElems = [];
    let cumulativeMeters = 0;
    const legs = _get(crew, "directions.legs", []);

    legs.forEach((leg, index) => {
      const { title, destination } = this.buildLegLabel(crew, leg, index);
      let stepElems = [];

      if (leg.steps && leg.steps.length) {
        leg.steps.forEach((step, i) => {
          cumulativeMeters += step.distance || 0;
          const cumulativeLabel = this.formatCumulativeMiles(cumulativeMeters);
          const instruction = _get(step, "maneuver.instruction", "");
          stepElems.push(
            <div
              key={`step-${index}-${i}`}
              className="step-item"
              aria-label={`${instruction}; ${cumulativeLabel} cumulative from route start`}
            >
              <span className="step-seq" title="Cumulative miles from route start">
                {cumulativeLabel}
              </span>
              <div className="step-body">
                <div className="step-instruction">{instruction}</div>
                <div className="step-meta">
                  {this.getMilesFromMeters(step.distance)} mi &middot;{" "}
                  {this.makeDurationReadable(step.duration)}
                </div>
              </div>
            </div>
          );
        });
      } else {
        cumulativeMeters += leg.distance || 0;
      }

      const legSummaryText = _get(leg, "summary", "");

      legElems.push(
        <div key={`leg-${index}`} className="column-list">
          <div className="leg-header">
            <span className="leg-number">{index + 1}</span>
            <div className="leg-label">
              <span className="leg-stop-kind">Dropoff</span>
              <h5>{title}</h5>
              {destination && (
                <div className="leg-destination">@ {destination}</div>
              )}
            </div>

            <div className="leg-route-header">
              <div className="leg-route-stats">
                {this.makeDurationReadable(leg.duration)} &middot;{" "}
                {this.getMilesFromMeters(leg.distance)} mi
              </div>
              {legSummaryText ? (
                <div className="leg-route-summary-text">{legSummaryText}</div>
              ) : null}
            </div>
          </div>
          <div className="leg-steps">{stepElems}</div>
        </div>
      );
    });
    return legElems;
  }

  render() {
    const { crew } = this.props;
    const { duration, distance } = this.getDirectionsTotals(crew);
    const legCount = _get(crew, "directions.legs", []).length;
    const hasTotals =
      legCount > 0 && duration != null && distance != null;
    const showDebug = isDirectionsDebug();
    const orderAudit = showDebug ? auditMapboxOrderVsCrew(crew) : null;
    const showOrderWarning =
      showDebug && orderAudit && legCount > 0 && !orderAudit.ok;
    const showOrderOk =
      showDebug && orderAudit && legCount > 0 && orderAudit.ok;

    return (
      <div className="directions-section">
        <div className="directions-section-title">
          Driving Directions
          {hasTotals ? (
            <div className="directions-totals-header">
              <span className="directions-totals-label">Full Route: </span>
              <span className="directions-totals-stats">
                {this.makeDurationReadable(duration)} &middot;{" "}
                {this.getMilesFromMeters(distance)} miles
              </span>
            </div>
          ) : null}
          {showOrderOk ? (
            <div
              className="directions-mapbox-order-ok"
              role="status"
            >
              <div className="directions-mapbox-order-ok-title">
                Leg order matches Mapbox visit order
              </div>
            </div>
          ) : null}
          {showOrderWarning ? (
            <div
              className="directions-mapbox-order-warning"
              role="alert"
            >
              <div className="directions-mapbox-order-warning-title">
                Leg order does not match Mapbox visit order
              </div>
              <ul className="directions-mapbox-order-warning-issues">
                {orderAudit.issues.map((msg, i) => (
                  <li key={`order-issue-${i}`}>{msg}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="wrap-list">{this.buildListDirections()}</div>
      </div>
    );
  }
}
